import React, { useMemo, useState } from 'react';
import { useOutletContext } from 'react-router';
import { SEOHelmet } from 'components/SEO';
import { SegmentedToggle } from 'components/SegmentedToggle/SegmentedToggle';
import SearchInput from 'components/SearchInput/SearchInput';
import { useGetLiveQuery } from 'store/services/play/playApi';
import { LIVE_POLL_INTERVAL_MS } from 'constants/play';
import { resolveAdSlot } from 'utils/play/adSlot';
import PlayNotFound from '../../PlayNotFound';
import { PlayLayout } from '../../layout/PlayLayout';
import type { TournamentOutletContext } from '../../PlayRoutes';
import { usePlaySocket } from '../../hooks/usePlaySocket';
import { useRoundClock } from '../../hooks/useRoundClock';
import { StandingsTable } from '../../components/StandingsTable';
import { AdCard } from '../../components/AdCard';
import { tournamentStatusLabel } from '../../playFormat';
import { TournamentLoading } from '../Tournament/TournamentHomePage';
import { standingsSubtitle } from '../Tournament/StandingsPage';
import '../Host/HostRound.css';
import './LivePage.css';

type LiveTab = 'pairings' | 'standings';

/**
 * Decision 24: the public spectator page, no login, usernames only. Watches the
 * room over the socket (cookie-less sockets are admitted) and polls GET /live
 * every 30 s as the resync (review ruling 4). noindex comes from the CloudFront
 * header (ruling E9); the Helmet tag is the in-app layer.
 */
const LivePage: React.FC = () => {
  const { code } = useOutletContext<TournamentOutletContext>();
  const [tab, setTab] = useState<LiveTab>('pairings');
  const [query, setQuery] = useState('');
  const { data, isLoading, error } = useGetLiveQuery(code, {
    pollingInterval: LIVE_POLL_INTERVAL_MS,
    skipPollingIfUnfocused: true,
  });
  usePlaySocket({ code, enabled: !!data, mode: 'live' });
  const clock = useRoundClock(data?.currentRound ?? null);

  const q = query.trim().toLowerCase();
  const pairings = useMemo(() => {
    const matches = data?.currentRound?.matches ?? [];
    if (!q) return matches;
    return matches.filter((m) => m.playerA.username.toLowerCase().includes(q) || (m.playerB?.username.toLowerCase().includes(q) ?? false));
  }, [data, q]);

  const status = (error as { status?: number } | undefined)?.status;
  if (status === 404) return <PlayNotFound reason="missing" />;
  if (isLoading || !data) return <TournamentLoading />;

  const { tournament, currentRound } = data;
  const live = tournament.status === 'LIVE' && !!currentRound?.startedAt;
  const slot = resolveAdSlot({ adsEnabled: data.adsEnabled, screen: 'live' });

  return (
    <PlayLayout title={tournament.name} subtitle={`${tournamentStatusLabel(tournament.status, currentRound?.number ?? null, currentRound?.totalRounds ?? tournament.swissRounds)} · ${tournament.activeCount} players`}>
      <SEOHelmet title={`${tournament.name} - Live - TradeMagic Play`} description="Live pairings and standings." noindex />

      <section className="play-card play-live__head" data-testid="live-head">
        <div>
          <p className="play-eyebrow">Live</p>
          <h1 className="play-title">{tournament.name}</h1>
          <p className="play-helper">{tournament.formatLabel} · {tournament.venueText || (tournament.venueType === 'ONLINE' ? 'Online' : '')}</p>
        </div>
        {currentRound && (
          <div className={`play-clock play-clock--${live ? clock.tone : 'normal'}`} data-testid="live-clock">
            <span className="play-clock__time">{clock.text}</span>
            <span className="play-clock__caption">
              Round {currentRound.number} of {currentRound.totalRounds} · {!live ? 'Not started' : clock.tone === 'overtime' ? 'Time called' : clock.isPaused ? 'Paused' : 'Time remaining'}
            </span>
          </div>
        )}
      </section>

      <div className="play-live__tabs">
        <SegmentedToggle<LiveTab>
          ariaLabel="Live view"
          value={tab}
          onChange={setTab}
          options={[
            { value: 'pairings', label: 'Pairings' },
            { value: 'standings', label: 'Standings' },
          ]}
        />
      </div>

      {tab === 'pairings' && (
        <section className="play-card" data-testid="live-pairings">
          {!currentRound ? (
            <p className="play-helper">Pairings go up when the organizer starts round 1.</p>
          ) : (
            <>
              <SearchInput value={query} onChange={setQuery} placeholder="Find your name" ariaLabel="Find your name" />
              <ul className="play-match-list">
                {pairings.map((m) => (
                  <li key={m.id}>
                    <div className="play-match-row" data-testid="live-pairing-row">
                      <span className="play-match-row__table">{m.isBye ? '—' : m.tableNumber}</span>
                      <span className="play-match-row__players">
                        <span className="play-seat">
                          <span className="play-seat__name">{m.playerA.username}</span>
                          {m.gamesA !== null && <span className="play-seat__score">{m.gamesA}</span>}
                        </span>
                        <span className="play-seat">
                          <span className="play-seat__name">{m.playerB?.username ?? 'Bye'}</span>
                          {m.gamesB !== null && <span className="play-seat__score">{m.gamesB}</span>}
                        </span>
                      </span>
                      <span className={`play-status-chip ${m.status === 'REPORTED' || m.isBye ? 'play-status-chip--reported' : ''}`}>{m.isBye ? 'Bye' : m.status === 'PENDING' ? 'Playing' : 'Done'}</span>
                    </div>
                  </li>
                ))}
                {pairings.length === 0 && (
                  <li>
                    <p className="play-helper">No table matches that name.</p>
                  </li>
                )}
              </ul>
            </>
          )}
        </section>
      )}

      {tab === 'standings' && <StandingsTable rows={data.standings} variant="public" subtitle={standingsSubtitle(currentRound ? [{ startedAt: currentRound.startedAt }] : [], !!currentRound && currentRound.resultsIn < currentRound.resultsTotal)} />}

      {slot && <AdCard slot={slot} code={code} formatLabel={tournament.format} />}
    </PlayLayout>
  );
};

export default LivePage;
